import { Alert, Button, Group, Modal, NumberInput, Select, Stack, TextInput } from '@mantine/core';
import { IconRecycle } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import condition from './Condition.module.css';
import { usePmsErrorText } from './EquipmentDrawer.js';
import work from './PmsWork.module.css';
import type { PmsSource } from './source.js';
import type { RotableList, RotableView } from './types.js';

/**
 * Component Details ▸ Rotables (DNV-CP-0206 Sec.2 [4.3] c; ADR 0133 §6): bu konumda takılı
 * dolaşan bileşenler ve kendi toplam saatleri; takma, sökme ve yeni parça kaydı (yalnız gemide,
 * yetkiye göre); konumdaki hareket geçmişi. Parçanın bütün dönemleri "History" penceresinde.
 */
export function RotablesPanel({
  source,
  vesselId,
  equipmentId,
  currentHours,
  today,
  initialList = null,
}: {
  source: Pick<PmsSource, 'rotables' | 'rotableEditor'>;
  vesselId: string;
  equipmentId: string;
  /** Konumun güncel sayacı (takma/sökme önerisi); sayaçsızda `null`. */
  currentHours: number | null;
  today: string;
  /** İlk görünüm (sunucu çizimi ve test); yükleme yine yapılır. */
  initialList?: RotableList | null;
}) {
  const { t, i18n } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) => t(`pms.rotables.${key}`, values);
  const errorText = usePmsErrorText();
  const locale = i18n.resolvedLanguage === 'tr' ? 'tr-TR' : 'en-US';
  const hours = (value: number | null) =>
    value === null
      ? '—'
      : t('pms.hours', {
          value: new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value),
        });
  const [list, setList] = useState<RotableList | null>(initialList);
  const [failed, setFailed] = useState<unknown>(null);
  const [dialog, setDialog] = useState<
    | { kind: 'install' }
    | { kind: 'remove'; part: RotableView }
    | { kind: 'register' }
    | { kind: 'history'; part: RotableView }
    | null
  >(null);

  const load = useCallback(async () => {
    if (!source.rotables) return;
    try {
      setList(await source.rotables(vesselId));
      setFailed(null);
    } catch (error) {
      setFailed(error);
    }
  }, [source, vesselId]);
  useEffect(() => {
    void load();
  }, [load]);

  if (failed) return <Alert color="red">{errorText(failed)}</Alert>;
  if (!list) return null;
  const editor = source.rotableEditor;
  const here = list.items.filter((r) => r.equipmentId === equipmentId);
  const spare = list.items.filter((r) => r.equipmentId === null);
  const moves = list.items
    .flatMap((r) =>
      r.movements.filter((m) => m.equipmentId === equipmentId).map((m) => ({ part: r, m })),
    )
    .sort(
      (a, b) =>
        b.m.movedOn.localeCompare(a.m.movedOn) || b.m.createdAt.localeCompare(a.m.createdAt),
    );
  const done = async () => {
    setDialog(null);
    await load();
  };

  return (
    <Stack gap="xs">
      {here.length === 0 ? (
        <EmptyState icon={<IconRecycle size={22} stroke={1.8} />} title={k('emptyHere')} />
      ) : (
        <ul className={work.detailList} aria-label={k('installedHere')}>
          {here.map((part) => (
            <li key={part.id}>
              <span className={work.detailListMain}>
                <strong>
                  {part.serialNo} · {part.name}
                </strong>
                <span className={work.note}>
                  {k('installedSince', { day: formatDay(part.installedOn ?? '') })} ·{' '}
                  {k('totalHours', { hours: hours(part.totalHours) })}
                  {part.hoursIncomplete ? ` · ${k('incomplete')}` : ''}
                </span>
              </span>
              <Group gap={4}>
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => setDialog({ kind: 'history', part })}
                >
                  {k('history')}
                </Button>
                {editor && list.can.move ? (
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => setDialog({ kind: 'remove', part })}
                  >
                    {k('remove')}
                  </Button>
                ) : null}
              </Group>
            </li>
          ))}
        </ul>
      )}
      {editor ? (
        <Group gap="xs">
          {list.can.move ? (
            <Button
              size="xs"
              disabled={spare.length === 0}
              onClick={() => setDialog({ kind: 'install' })}
            >
              {k('install')}
            </Button>
          ) : null}
          {list.can.create ? (
            <Button size="xs" variant="default" onClick={() => setDialog({ kind: 'register' })}>
              {k('register')}
            </Button>
          ) : null}
        </Group>
      ) : null}
      {moves.length > 0 ? (
        <>
          <h4 className={condition.sectionTitle}>{k('movementsHere')}</h4>
          <ul className={work.detailList}>
            {moves.slice(0, 12).map(({ part, m }) => (
              <li key={m.id}>
                <span className={work.detailListMain}>
                  <strong>
                    {k(`kinds.${m.kind}`)} · {part.serialNo}
                  </strong>
                  <span className={work.note}>
                    {formatDay(m.movedOn)} · {hours(m.positionHours)}
                    {m.byName ? ` · ${m.byName}` : ''}
                    {m.reason ? ` · ${m.reason}` : ''}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {dialog?.kind === 'install' || dialog?.kind === 'remove' ? (
        <MovementDialog
          kind={dialog.kind}
          parts={dialog.kind === 'remove' ? [dialog.part] : spare}
          today={today}
          currentHours={currentHours}
          onClose={() => setDialog(null)}
          onSave={async (partId, values) => {
            await editor?.move(partId, { ...values, kind: dialog.kind, equipmentId });
            await done();
          }}
        />
      ) : null}
      {dialog?.kind === 'register' && editor ? (
        <RegisterDialog
          today={today}
          onClose={() => setDialog(null)}
          onSave={async (values) => {
            await editor.register(values);
            await done();
          }}
        />
      ) : null}
      {dialog?.kind === 'history' ? (
        <Modal
          opened
          onClose={() => setDialog(null)}
          title={`${dialog.part.serialNo} · ${dialog.part.name}`}
        >
          <Stack gap="xs">
            <span>
              {k('totalHours', { hours: hours(dialog.part.totalHours) })} ·{' '}
              {k('initialHours', { hours: hours(dialog.part.initialHours) })}
            </span>
            <ul className={work.detailList} aria-label={k('periods')}>
              {dialog.part.periods.map((p) => (
                <li key={`${p.equipmentId}-${p.installedOn}`}>
                  <span className={work.detailListMain}>
                    <strong>{p.equipmentName ?? p.equipmentId}</strong>
                    <span className={work.note}>
                      {formatDay(p.installedOn)} – {p.removedOn ? formatDay(p.removedOn) : k('now')}{' '}
                      · {hours(p.hours)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </Stack>
        </Modal>
      ) : null}
    </Stack>
  );
}

function MovementDialog({
  kind,
  parts,
  today,
  currentHours,
  onClose,
  onSave,
}: {
  kind: 'install' | 'remove';
  parts: readonly RotableView[];
  today: string;
  currentHours: number | null;
  onClose: () => void;
  onSave: (
    partId: string,
    values: { movedOn: string; positionHours: number | null; reason: string | null },
  ) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.rotables.${key}`);
  const errorText = usePmsErrorText();
  const [partId, setPartId] = useState<string | null>(
    parts.length === 1 ? (parts[0]?.id ?? null) : null,
  );
  const [movedOn, setMovedOn] = useState<string | null>(today);
  const [positionHours, setPositionHours] = useState<number | string>(currentHours ?? '');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal opened onClose={onClose} title={k(kind === 'install' ? 'install' : 'remove')}>
      <Stack gap="sm">
        <Select
          label={k('part')}
          data={parts.map((p) => ({ value: p.id, label: `${p.serialNo} · ${p.name}` }))}
          value={partId}
          onChange={setPartId}
          disabled={kind === 'remove'}
          searchable
          required
        />
        <DayInput
          label={k('movedOn')}
          value={movedOn}
          maxDate={today}
          onChange={setMovedOn}
          required
        />
        <NumberInput
          label={k('positionHours')}
          description={k('positionHoursHint')}
          value={positionHours}
          onChange={setPositionHours}
          min={0}
          decimalScale={1}
        />
        <TextInput
          label={k('reason')}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        {error ? <Alert color="red">{errorText(error)}</Alert> : null}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('pms.drawer.cancel')}
          </Button>
          <Button
            loading={busy}
            disabled={!partId || !movedOn}
            onClick={async () => {
              if (!partId || !movedOn) return;
              setBusy(true);
              try {
                await onSave(partId, {
                  movedOn,
                  positionHours: typeof positionHours === 'number' ? positionHours : null,
                  reason: reason.trim() || null,
                });
              } catch (failure) {
                setError(failure);
              } finally {
                setBusy(false);
              }
            }}
          >
            {t('pms.drawer.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function RegisterDialog({
  today,
  onClose,
  onSave,
}: {
  today: string;
  onClose: () => void;
  onSave: (values: {
    serialNo: string;
    name: string;
    partNo: string | null;
    maker: string | null;
    initialHours: number;
    registeredOn: string;
    notes: string | null;
  }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.rotables.${key}`);
  const errorText = usePmsErrorText();
  const [values, setValues] = useState({
    serialNo: '',
    name: '',
    partNo: '',
    maker: '',
    notes: '',
  });
  const [initialHours, setInitialHours] = useState<number | string>(0);
  const [registeredOn, setRegisteredOn] = useState<string | null>(today);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const field = (name: keyof typeof values, required = false) => (
    <TextInput
      label={k(name)}
      required={required}
      value={values[name]}
      onChange={(e) => {
        const value = e.currentTarget.value;
        setValues((v) => ({ ...v, [name]: value }));
      }}
    />
  );
  return (
    <Modal opened onClose={onClose} title={k('register')}>
      <Stack gap="sm">
        {field('serialNo', true)}
        {field('name', true)}
        {field('partNo')}
        {field('maker')}
        <NumberInput
          label={k('initialHoursLabel')}
          description={k('initialHoursHint')}
          value={initialHours}
          onChange={setInitialHours}
          min={0}
          decimalScale={1}
        />
        <DayInput
          label={k('registeredOn')}
          value={registeredOn}
          maxDate={today}
          onChange={setRegisteredOn}
          required
        />
        {field('notes')}
        {error ? <Alert color="red">{errorText(error)}</Alert> : null}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('pms.drawer.cancel')}
          </Button>
          <Button
            loading={busy}
            disabled={!values.serialNo.trim() || !values.name.trim() || !registeredOn}
            onClick={async () => {
              if (!registeredOn) return;
              setBusy(true);
              const text = (v: string) => v.trim() || null;
              try {
                await onSave({
                  serialNo: values.serialNo.trim(),
                  name: values.name.trim(),
                  partNo: text(values.partNo),
                  maker: text(values.maker),
                  initialHours: typeof initialHours === 'number' ? initialHours : 0,
                  registeredOn,
                  notes: text(values.notes),
                });
              } catch (failure) {
                setError(failure);
              } finally {
                setBusy(false);
              }
            }}
          >
            {t('pms.drawer.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
